import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

interface ResourceUsage {
    cores?: number
    memory?: number
    volumes?: number
    networks?: number
}

interface ServiceQuotasData {
    quotas?: {
        cores?: number
        memory?: number
        volumes?: number
        networks?: number
        instances?: number
        backups?: number
        snapshots?: number
    }
    resources?: ResourceUsage[]
    volumes?: { attached?: boolean; size?: string }[]
    lbs?: unknown[]
    snapshots?: unknown[]
    reserves?: { name?: string }[]
    backups?: unknown[]
    instances?: unknown[]
}

interface CloudInfoSummaryProps {
    providerAddons: {
        provider_id: number
        provider_scope: string
        service_name: string
        service_type: string
        service_id: number
        customer_id: number
        tenant: string
        metadata: {
            provider_name?: string
            provider_scope?: string
            zone?: string
            type?: string
            identifer?: string
            credential?: string
        }
        addons?: unknown[]
    }
    serviceQuotas?: ServiceQuotasData
}

/** Compact quota card with progress bar */
const QuotaCard = ({
    title,
    usage,
    limit,
    unit,
}: {
    title: string
    usage: number | string
    limit?: number | null
    unit?: string
}) => {
    const numUsage = typeof usage === 'string' ? parseFloat(usage) || 0 : usage
    const percent = limit ? Math.min((numUsage / limit) * 100, 100) : 0
    const limitText = limit
        ? `${limit.toLocaleString()}${unit ? ` ${unit}` : ''}`
        : '-'

    return (
        <div className="bg-gray-100 rounded-sm px-3 py-2">
            <div className="flex justify-between items-baseline">
                <Typogaphy className="text-xs font-medium text-gray-600">
                    {title}
                </Typogaphy>
                <Typogaphy className="text-sm font-semibold text-gray-900">
                    {typeof usage === 'number' ? usage.toLocaleString() : usage}
                    <span className="text-xs font-normal text-gray-500">
                        {' / '}
                        {limitText}
                    </span>
                </Typogaphy>
            </div>
            {limit != null && (
                <Progress
                    value={percent}
                    className={cn(
                        'h-1 mt-1.5',
                        percent > 80
                            ? '[&>div]:bg-red-500'
                            : '[&>div]:bg-primary'
                    )}
                />
            )}
        </div>
    )
}

/** Renders cloud provider metadata + quota usage as a section (no card wrapper) */
const CloudInfoSummary = ({
    providerAddons,
    serviceQuotas,
}: CloudInfoSummaryProps) => {
    const { t } = useTranslation('services')
    const meta = providerAddons.metadata

    // Compute resource usage from instances (same logic as partner useResourceCalculator)
    const resourceUsage = useMemo(() => {
        const resources = serviceQuotas?.resources || []
        const volumes = serviceQuotas?.volumes?.filter((v) => !v.attached) || []

        const cores = resources.reduce((sum, r) => sum + (r.cores || 0), 0)
        const memory = resources.reduce((sum, r) => sum + (r.memory || 0), 0)
        const volumeFromResources = resources.reduce(
            (sum, r) => sum + (r.volumes || 0),
            0
        )
        const volumeFromUnattached = volumes.reduce(
            (sum, v) => sum + (Number(v.size) || 0) / 1024,
            0
        )
        const network = resources.reduce((sum, r) => sum + (r.networks || 0), 0)
        const reservePublicIPs =
            serviceQuotas?.reserves?.filter((r) => r.name?.match(/public/))
                ?.length || 0

        return {
            cores,
            memory,
            volumes: volumeFromResources + volumeFromUnattached,
            networks: network + reservePublicIPs,
        }
    }, [serviceQuotas])

    const quotas = serviceQuotas?.quotas

    return (
        <>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    <Typogaphy className="font-medium text-gray-900">
                        {providerAddons.service_name}
                    </Typogaphy>
                    <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={providerAddons.service_type}
                        className="uppercase"
                    />
                </div>
                {providerAddons.addons && providerAddons.addons.length > 0 && (
                    <Chip
                        size="small"
                        variant="outlined"
                        color="info"
                        label={`${providerAddons.addons.length} addon(s)`}
                    />
                )}
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-3 border-t border-gray-200 text-sm">
                {meta?.provider_name && (
                    <Typogaphy className="text-gray-600">
                        {t('provider', { defaultValue: 'Provider' })}:{' '}
                        <span className="font-medium text-gray-900">
                            {meta.provider_name}
                        </span>
                    </Typogaphy>
                )}
                <Typogaphy className="text-gray-600">
                    Scope:{' '}
                    <span className="font-medium text-gray-900">
                        {providerAddons.provider_scope}
                    </span>
                </Typogaphy>
                <Typogaphy className="text-gray-600">
                    Tenant:{' '}
                    <span className="font-medium text-gray-900">
                        {providerAddons.tenant}
                    </span>
                </Typogaphy>
                <Typogaphy className="text-gray-600">
                    Service ID:{' '}
                    <span className="font-medium text-gray-900">
                        {providerAddons.service_id}
                    </span>
                </Typogaphy>
            </div>

            {/* Resource Quotas */}
            {serviceQuotas && (
                <div className="pt-3 mt-3 border-t border-gray-200">
                    <Typogaphy className="text-sm font-medium text-gray-700 mb-2">
                        {t('quotas', { defaultValue: 'Quotas' })}
                    </Typogaphy>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <QuotaCard
                            title="vCPU"
                            usage={resourceUsage.cores}
                            limit={quotas?.cores}
                        />
                        <QuotaCard
                            title={t('memory', { defaultValue: 'Memory' })}
                            usage={resourceUsage.memory}
                            limit={quotas?.memory}
                            unit="GiB"
                        />
                        <QuotaCard
                            title="Volume"
                            usage={Math.round(resourceUsage.volumes)}
                            limit={quotas?.volumes}
                            unit="GiB"
                        />
                        <QuotaCard
                            title="Network"
                            usage={resourceUsage.networks}
                            limit={quotas?.networks}
                        />
                        <QuotaCard
                            title="Instance"
                            usage={serviceQuotas.instances?.length || 0}
                            limit={quotas?.instances}
                        />
                        <QuotaCard
                            title="Backup"
                            usage={serviceQuotas.backups?.length || 0}
                            limit={quotas?.backups}
                        />
                        <QuotaCard
                            title="Load Balancer"
                            usage={serviceQuotas.lbs?.length || 0}
                            limit={null}
                        />
                        <QuotaCard
                            title="Snapshot"
                            usage={serviceQuotas.snapshots?.length || 0}
                            limit={null}
                        />
                    </div>
                </div>
            )}
        </>
    )
}

export default CloudInfoSummary
